import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Form } from "antd";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import "./style.css";
import {
    clearUpdateProjectAction,
    getAnalogProjectByIdAction,
    updateAnalogObjectAction,
    updateAnalogObjectFilesAction,
} from "../../store/actions";
import CustomInput from "../../../../components/CustomInput";
import CustomButton from "../../../../components/CustomButton";
import Notifications from "../../../../helpers/Notifications";
import EditPtmDescription from "./editPtmDescription";
import EditProjectFiles from "./editFiles";

const EditProject = () => {
    const [objectId, setObjectId] = useState(null);
    const [deletedFiles, setDeletedFiles] = useState([]);
    const [descriptions, setDescriptions] = useState([]);
    const [isFormChanged, setIsFormChanged] = useState(false);

    const { t } = useTranslation(["common", "action", "project", "valid"]);
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [formInfo] = Form.useForm();
    const [fileForm] = Form.useForm();

    const { oneProject, objectUpdated, ptmsWithDescription } = useSelector((state) => state.projectReducer);

    useEffect(() => {
        setIsFormChanged(false)
        dispatch(getAnalogProjectByIdAction({ AnalogProjectId: id }));
        return () => {
            dispatch(clearUpdateProjectAction());
        };
    }, []);

    useEffect(() => {
        if (oneProject) {
            setObjectId(oneProject.analogObjectId);
            formInfo.setFieldsValue({
                customer: oneProject.customer,
                designer: oneProject.designer,
            });
        }
    }, [oneProject]);

    useEffect(() => {
        if (objectUpdated) {
            Notifications.successNotice(t("action:updated"), "", 10);
            navigate(`/project/${id}`);
        }
    }, [objectUpdated]);

    const updateFiles = () => {
        fileForm.validateFields().then((values) => {
            let data = new FormData();
            data.append("ObjectId", objectId);
            deletedFiles.forEach((fileId) =>
                data.append("DeletedFiles", fileId)
            );
            Object.entries(values).forEach(([funcType, files]) => {
                files?.forEach((file) => {
                    if (file.originFileObj)
                        data.append(funcType, file.originFileObj);
                });
            });

            dispatch(updateAnalogObjectFilesAction(data));
        });
    };

    const updateProject = () => {
        formInfo.validateFields().then((values) => {
            const data = {
                ObjectId: objectId,
                Customer: values.customer,
                Designer: values.designer,
                PtmDescriptions: Object.entries(values.descriptions).map(
                    ([ptmId, description]) => {
                        return {
                            PtmId: ptmId,
                            Description: description,
                        };
                    }
                ),
            };
            dispatch(updateAnalogObjectAction(data));
        });
    };

    const formChange = (_, updateValues) => {
        const changed = {
            fileForm: false,
            formInfo: false,
        };
        const isFileAdded = !!Object.entries(fileForm.getFieldsValue()).find(
            ([_, files]) => !!files?.find((file) => !!file.originFileObj))

        changed.fileForm = !!deletedFiles?.length || isFileAdded;

        if (!changed.fileForm && ptmsWithDescription && !("mainImage" in updateValues)) {
            const previousValues = {
                customer: oneProject.customer,
                designer: oneProject.designer,
                descriptions: Object.fromEntries(
                    ptmsWithDescription?.filter((p) => p.description).map((p) => [p.id, p.description])
                ),
            };

            if (Array.isArray(updateValues)) {
                updateValues = Object.fromEntries(
                    updateValues?.filter((p) => p.description != null).map((p) => [p.id, p.description])
                );

                changed.formInfo = JSON.stringify(updateValues) !== JSON.stringify(previousValues.descriptions);
            } else {
                changed.formInfo = JSON.stringify(updateValues) !== JSON.stringify(previousValues);
            }
        }

        setIsFormChanged(changed.fileForm || changed.formInfo);
    };

    const onFinish = () => {
        if (!isFormChanged) {
            Notifications.infoNotice("valid:sameData", "", 10)
        } else {
            updateFiles();
            updateProject();
        }
    };

    return (
        <div className="edit-project">
            <div className="edit-project-header">
                <Link className="back" to={`/project/${id}`}>
                    <img alt="" src="/Arrow.svg" />
                    {t("common:back")}
                </Link>
                <p>{oneProject?.name}</p>
            </div>

            <EditProjectFiles
                objectId={objectId}
                projectId={id}
                form={fileForm}
                formChange={formChange}
                deletedFiles={deletedFiles}
                setDeletedFiles={setDeletedFiles}
            />

            <Form
                layout="horizontal"
                autoComplete="off"
                form={formInfo}
                className="edit-project-form"
                name="projectEditForm"
                onValuesChange={formChange}
            >
                <div className="edit-project-info">
                    <Form.Item
                        label={`${t("project:customer")}:`}
                        name="customer"
                        rules={[{ required: true }]}
                    >
                        <CustomInput
                            maxLength={250}
                            placeholder={t("project:enterCustomer")}
                        />
                    </Form.Item>
                    <Form.Item
                        label={`${t("project:designer")}:`}
                        name="designer"
                        rules={[{ required: true }]}
                    >
                        <CustomInput
                            maxLength={250}
                            placeholder={t("project:enterDesigner")}
                        />
                    </Form.Item>
                </div>

                <EditPtmDescription
                    descriptions={descriptions}
                    setDescriptions={setDescriptions}
                    form={formInfo}
                    formChange={formChange}
                    objectId={objectId}
                />
            </Form>

            {isFormChanged &&
                <CustomButton
                    className="edit-project-save"
                    type="green"
                    onClick={onFinish}
                >
                    {t("common:saveChanges")}
                </CustomButton>
            }
        </div>
    );
};

export default EditProject;
